import { FileCopyOutlined as FileCopyOutlinedIcon, MoreVert as MoreVertIcon } from "@mui/icons-material";
import {
	Box,
	IconButton,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { useState } from "react";
import { LoadingComp } from "../../../shared/components/loading/Loading";
import { globalFormatCurrencyBRL, globalMaskDocument } from "../../../shared/utils";
import { IComissionData } from "../types/types";
import type { ComissionMenuModalType } from "./Menus/ComissionMenu";
import { ComissionMenu } from "./Menus/ComissionMenu";
import { ModalConfirmExport } from "./Modals/ModalConfirmExportComission";
import { ModalDetailComission } from "./Modals/ModalDetailComission";
import { ModalRegisterComission } from "./Modals/ModalRegisterComission";
import { ModalSelectComission } from "./Modals/ModalSelectComission";

interface Props {
	rows: IComissionData[];
	totalCount: number;
	page: number;
	rowsPerPage: number;
	onPageChange: (newPage: number) => void;
	onRowsPerPageChange: (value: number) => void;
	dataIni: string;
	dataFim: string;
	onRefresh: () => void;
	selectedClientIds: string[];
	onToggleSelect: (clientId: string) => void;
	onToggleSelectAll: () => void;
}

export const TableComission = ({ rows, totalCount, page, rowsPerPage, onPageChange, onRowsPerPageChange, dataIni, dataFim, onRefresh }: Props) => {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [hoveredDocument, setHoveredDocument] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [selectedRow, setSelectedRow] = useState<IComissionData | null>(null);
	const [modalType, setModalType] = useState<ComissionMenuModalType | null>(null);

	const handleCopy = (value: string) => navigator.clipboard.writeText(value).catch(console.error);

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: IComissionData) => {
		setAnchorEl(event.currentTarget);
		setSelectedRow(row);
	};

	const handleOpenModal = (type: ComissionMenuModalType, row: IComissionData) => {
		setSelectedRow(row);
		setModalType(type);
	};

	const handleCloseModal = () => {
		setSelectedRow(null);
		setModalType(null);
	};

	return (
		<>
			<TableContainer
				component={Paper}
				variant="elevation"
				sx={{ mt: 1, width: "96%", px: { xs: 1, sm: 3 }, py: { xs: 2, sm: 3 }, ml: 1, borderRadius: 2, overflow: "hidden" }}
			>
				<Table size="small" stickyHeader aria-label="Tabela de Assinaturas">
					<TableHead>
						<TableRow sx={{ backgroundColor: "grey.100" }}>
							{["Parceiro", "Valor Total", "Registros", "Ações"].map((header, i) => (
								<TableCell key={i} align={i === 2 || i === 3 ? "center" : i === 4 ? "right" : "left"}>
									<Typography variant="button">{header}</Typography>
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map((row) => {
							return (
								<TableRow key={row.clientId}>
									<TableCell>
										<Stack spacing={0.5}>
											<Typography fontWeight="bold">{row.clientName}</Typography>
											<Typography
												variant="body2"
												color="textSecondary"
												noWrap
												onMouseEnter={() => setHoveredDocument(row.clientId)}
												onMouseLeave={() => setHoveredDocument(null)}
											>
												{globalMaskDocument(row.clientDocument)}
												{hoveredDocument === row.clientId && (
													<Tooltip title="Copiar Documento">
														<IconButton sx={{ fontSize: 10, ml: 1 }} onClick={() => handleCopy(row.clientDocument)}>
															<FileCopyOutlinedIcon sx={{ fontSize: 12 }} />
														</IconButton>
													</Tooltip>
												)}
											</Typography>
										</Stack>
									</TableCell>
									<TableCell>
										<Typography fontWeight={500} color="textSecondary">
											{globalFormatCurrencyBRL(row.comissionValue)}
										</Typography>
									</TableCell>
									<TableCell align="center">
										<Typography variant="body2" color="textSecondary">
											{row.totalComissionCount}
										</Typography>
									</TableCell>

									<TableCell align="right">
										<IconButton onClick={(e) => handleMenuOpen(e, row)}>
											<MoreVertIcon />
										</IconButton>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>

				<Box display="flex" justifyContent="flex-end" alignItems="center" mt={2} pr={2}>
					<TablePagination
						component="div"
						count={totalCount}
						page={page}
						rowsPerPage={rowsPerPage}
						onPageChange={(_, newPage) => onPageChange(newPage)}
						onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
						rowsPerPageOptions={[10, 25, 50]}
						labelRowsPerPage="Linhas por página:"
						labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
					/>
				</Box>

				<ComissionMenu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} selectedRow={selectedRow} onOpenModal={handleOpenModal} />
			</TableContainer>

			{selectedRow && modalType === "launch" && (
				<>
					<ModalRegisterComission
						visible={true}
						onClose={handleCloseModal}
						rowsInstallment={{ ...selectedRow, id: selectedRow.clientId }}
						onSuccess={onRefresh}
					/>
				</>
			)}

			{selectedRow && modalType === "details" && (
				<ModalDetailComission open={true} onClose={handleCloseModal} comissionId={selectedRow.clientId} dataIni={dataIni} dataFim={dataFim} />
			)}

			{selectedRow && modalType === "export" && (
				<ModalConfirmExport
					open={true}
					onClose={handleCloseModal}
					comissionId={selectedRow.clientId}
					clientName={selectedRow.clientName}
					dataIni={dataIni}
					dataFim={dataFim}
					exportType="excel"
				/>
			)}

			{selectedRow && modalType === "pdf" && (
				<ModalConfirmExport
					open={true}
					onClose={handleCloseModal}
					comissionId={selectedRow.clientId}
					clientName={selectedRow.clientName}
					dataIni={dataIni}
					dataFim={dataFim}
					exportType="pdf"
				/>
			)}

			{selectedRow && modalType === "manualPayment" && (
				<ModalSelectComission
					open={true}
					onClose={handleCloseModal}
					dataIni={dataIni}
					dataFim={dataFim}
					row={{ clientId: selectedRow.clientId, name: selectedRow.clientName }}
					modalType="manual"
					filterByPaymentMethod="billet"
					onSuccess={onRefresh}
				/>
			)}

			{selectedRow && modalType === "cancel" && (
				<ModalSelectComission
					open={true}
					onClose={handleCloseModal}
					dataIni={dataIni}
					dataFim={dataFim}
					row={{ clientId: selectedRow.clientId, name: selectedRow.clientName }}
					modalType="cancel"
					filterByPaymentMethod="billet" 
					onSuccess={onRefresh}
				/>
			)}

			<LoadingComp isopen={isLoading} />
		</>
	);
};
